import React, { useState, useEffect } from "react";
import { db } from "../../firebase/config";
import { doc, updateDoc, collection, query, where, getDocs, arrayUnion } from "firebase/firestore";
import { useAuth } from "../../context/AuthContext";
import { X, Plus, Trash2, Loader2, ListPlus, ArrowUp, ArrowDown } from "lucide-react";
import POLineMarkupModal from "./POLineMarkupModal";
import { calculateVendorShippingMultipliers, recalculateItemClientPrice, recalculateAllPoItemClientPrices, getDefaultTaxRate, calculateTotalCostSum, calculateNewDecimal } from "../../utils/pricingUtils";
import { openDocumentPrintWindow } from "../../utils/pdfExportUtils";
import { useVendors } from "../../hooks/useVendors";

export default function ChangeRequestModal({ po, onClose }) {
  const { currentUser } = useAuth();
  const { vendors } = useVendors();
  
  // Basic PO Info States
  const [title, setTitle] = useState(po.title || "");
  const [poNumber, setPoNumber] = useState(po.poNumber || "");
  const [customerPoNumber, setCustomerPoNumber] = useState(po.customerPoNumber || "");
  const [receivedFromCustomerDate, setReceivedFromCustomerDate] = useState(po.receivedFromCustomerDate || po.date || "");
  const [expirationDate, setExpirationDate] = useState(po.expirationDate || po.dueDate || "");
  const [estShippingCost, setEstShippingCost] = useState(po.estShippingCost !== undefined && po.estShippingCost !== null ? po.estShippingCost : 0);
  const [markupRate, setMarkupRate] = useState(po.markupRate !== undefined && po.markupRate !== null ? po.markupRate : 15);
  const [isPriority, setIsPriority] = useState(!!po.isPriority);
  
  // Line Items State
  const [items, setItems] = useState(po.items || []);

  // Synchronize state whenever po prop updates or modal is re-opened
  useEffect(() => {
    if (!po) return;
    setTitle(po.title || "");
    setPoNumber(po.poNumber || "");
    setCustomerPoNumber(po.customerPoNumber || "");
    setReceivedFromCustomerDate(po.receivedFromCustomerDate || po.date || "");
    setExpirationDate(po.expirationDate || po.dueDate || "");
    setEstShippingCost(po.estShippingCost !== undefined && po.estShippingCost !== null ? po.estShippingCost : 0);
    setMarkupRate(po.markupRate !== undefined && po.markupRate !== null ? po.markupRate : 15);
    setIsPriority(!!po.isPriority);
    setItems(po.items || []);
    setError("");
    setSuccess("");
  }, [po]);
  const [users, setUsers] = useState([]);
  
  const [isSaving, setIsSaving] = useState(false);
  const [customMarkupModalOpen, setCustomMarkupModalOpen] = useState(false);
  const [selectedMarkupItem, setSelectedMarkupItem] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Insert line popover menu state
  const [activeInsertMenu, setActiveInsertMenu] = useState(null); // { itemId, index }

  useEffect(() => {
    if (!activeInsertMenu) return;
    const handleOutsideClick = (e) => {
      if (!e.target.closest(".insert-line-popover-container")) {
        setActiveInsertMenu(null);
      }
    };
    window.addEventListener("click", handleOutsideClick);
    return () => window.removeEventListener("click", handleOutsideClick);
  }, [activeInsertMenu]);

  // Fetch Users for Staff dropdown (supporting roles array)
  useEffect(() => {
    async function fetchUsers() {
      try {
        const querySnapshot = await getDocs(collection(db, "users"));
        const list = [];
        querySnapshot.forEach(doc => {
          const u = doc.data();
          const hasProcurementRole = (Array.isArray(u.roles) && u.roles.includes("Procurement")) || u.role === "Procurement";
          if (hasProcurementRole && !u.deleted && u.status !== "Disabled") {
            const val = u.email ? u.email.split("@")[0].toLowerCase() : (u.username || u.name?.toLowerCase() || doc.id);
            const label = u.name || u.fullName || (u.email ? u.email.split("@")[0] : "User");
            list.push({ value: val, label, email: u.email || "", rawName: u.name || u.fullName || "" });
          }
        });
        if (list.length > 0) {
          setUsers(list);
        }
      } catch (err) {
        console.error("Error fetching users for Change Request modal:", err);
      }
    }
    fetchUsers();
  }, []);

  const getMatchedStaffValue = (staffVal) => {
    if (!staffVal) return "";
    const lower = String(staffVal).trim().toLowerCase();
    const matched = users.find(u => 
      u.value.toLowerCase() === lower || 
      (u.label && u.label.toLowerCase() === lower) ||
      (u.email && u.email.toLowerCase() === lower) ||
      (u.rawName && u.rawName.toLowerCase() === lower)
    );
    return matched ? matched.value : staffVal;
  };

  // Pricing helper functions
  const getPoMarkupRate = () => {
    if (markupRate !== undefined && markupRate !== null) {
      return parseFloat(markupRate);
    }
    return 15;
  };

  const getPoCcFee = () => {
    const savedFee = localStorage.getItem("global_cc_fee") || localStorage.getItem("global_non_cc_discount");
    return savedFee !== null ? parseFloat(savedFee) : 3;
  };

  const isCcPayment = () => {
    return Boolean(po && po.paymentMethod && po.paymentMethod.trim().toLowerCase() === "credit card");
  };

  const getEffectivePoMarkupRate = () => {
    const baseMarkup = getPoMarkupRate();
    if (isCcPayment()) {
      const ccFee = getPoCcFee();
      return baseMarkup + ccFee;
    }
    return baseMarkup;
  };

  const getItemMarkup = (item) => {
    if (item && item.isMarkupCustom && item.markupPercentage !== undefined && item.markupPercentage !== null) {
      return Number(item.markupPercentage);
    }
    return getEffectivePoMarkupRate();
  };

  const getNextLineNumber = () => {
    if (!items || items.length === 0) return "01";
    const lastItem = items[items.length - 1];
    const lastNumStr = lastItem.lineNumber || "0";
    const lastNum = parseInt(lastNumStr, 10);
    if (isNaN(lastNum)) return "01";
    const nextNum = lastNum + 1;
    return nextNum < 10 ? `0${nextNum}` : String(nextNum);
  };

  const findExistingVendorTax = (targetVendorName) => {
    if (!targetVendorName || !targetVendorName.trim()) return null;
    const vendorNorm = targetVendorName.trim().toLowerCase();
    
    if (items) {
      for (const it of items) {
        const vList = it.vendors && it.vendors.length > 0 ? it.vendors : [{ vendor: it.vendor, tax: it.tax }];
        for (const spl of vList) {
          if (spl.vendor && spl.vendor.trim().toLowerCase() === vendorNorm) {
            if (spl.tax !== undefined && spl.tax !== null && String(spl.tax).trim() !== "" && String(spl.tax).trim() !== "0.00" && String(spl.tax).trim() !== "0") {
              return spl.tax;
            }
          }
        }
      }
    }
    return null;
  };

  // Add line item
  const handleAddItem = () => {
    const nextLineNum = getNextLineNumber();
    const effectiveMarkup = getEffectivePoMarkupRate();
    const totalItemsCount = items.length + 1;
    const estShipping = Number(estShippingCost || 0);

    const newItem = {
      id: `li-${Date.now()}`,
      lineNumber: nextLineNum,
      description: "",
      quantity: 1,
      vendor: "",
      vendorPrice: "0.00",
      isTaxable: true,
      tax: true,
      shippingCost: "0.00",
      staff: currentUser?.email?.split("@")[0] || "",
      url: "",
      unitPrice: "0.00"
    };

    const newItems = [...items, newItem];
    const updatedItems = recalculateAllPoItemClientPrices(newItems, item => getItemMarkup(item), getDefaultTaxRate());
    setItems(updatedItems);
  };

  // Insert line item directly between rows
  const handleInsertItem = (targetIndex, position = "below") => {
    const insertIdx = position === "above" ? Math.max(0, targetIndex) : targetIndex + 1;

    const newItem = {
      id: `li-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      lineNumber: "",
      description: "",
      quantity: 1,
      vendor: "",
      vendorPrice: "0.00",
      isTaxable: true,
      tax: true,
      shippingCost: "0.00",
      staff: currentUser?.email?.split("@")[0] || "",
      url: "",
      unitPrice: "0.00"
    };

    const spliced = [
      ...items.slice(0, insertIdx),
      newItem,
      ...items.slice(insertIdx)
    ];

    // Re-sequence line numbers (01, 02, 03...)
    const resequencedItems = spliced.map((item, idx) => ({
      ...item,
      lineNumber: idx + 1 < 10 ? `0${idx + 1}` : String(idx + 1)
    }));

    const updatedItems = recalculateAllPoItemClientPrices(resequencedItems, item => getItemMarkup(item), getDefaultTaxRate());
    setItems(updatedItems);
  };

  // Delete line item
  const handleDeleteItem = (itemId) => {
    const remainingItems = items.filter(item => item.id !== itemId);
    const updatedItems = recalculateAllPoItemClientPrices(remainingItems, item => getItemMarkup(item));
    setItems(updatedItems);
  };

  const handleSaveLineMarkup = (markupConfig) => {
    if (!selectedMarkupItem) return;

    const rawUpdatedItems = items.map(item => {
      if (item.id !== selectedMarkupItem.id) return item;
      
      let updatedItem;
      if (!markupConfig) {
        updatedItem = {
          ...item,
          markupType: null,
          markupPercentage: null,
          markupDollar: null,
          isMarkupCustom: false
        };
      } else if (markupConfig.type === "dollar") {
        updatedItem = {
          ...item,
          markupType: "dollar",
          markupDollar: markupConfig.value,
          markupPercentage: null,
          isMarkupCustom: true
        };
      } else {
        updatedItem = {
          ...item,
          markupType: "percent",
          markupPercentage: markupConfig.value,
          markupDollar: null,
          isMarkupCustom: true
        };
      }
      return updatedItem;
    });

    const updatedItems = recalculateAllPoItemClientPrices(rawUpdatedItems, item => getItemMarkup(item));
    setItems(updatedItems);
    setCustomMarkupModalOpen(false);
    setSelectedMarkupItem(null);
  };

  // Handle cell inputs change
  const handleUpdateItemField = (itemId, field, value) => {
    const defaultTaxRate = getDefaultTaxRate();
    const rawUpdatedItems = items.map(item => {
      if (item.id !== itemId) return item;

      let parsedValue = value;
      if (field === "quantity") {
        parsedValue = value === "" ? "" : Number(value);
      }

      const updated = { ...item, [field]: parsedValue };
      if (field === "vendor" && value && value.trim()) {
        const normVal = value.trim().toLowerCase();
        const matchedVendor = (vendors || []).find(vend => (vend.name || "").trim().toLowerCase() === normVal);
        if (matchedVendor) {
          const isExempt = String(matchedVendor.taxExempt).toLowerCase() === "yes" || matchedVendor.taxExempt === true;
          if (isExempt) {
            updated.isTaxable = false;
            updated.tax = 0;
          } else {
            updated.isTaxable = true;
            updated.tax = defaultTaxRate;
          }
        }
      }

      if (updated.vendors && updated.vendors.length === 1) {
        if (["vendor", "vendorPrice", "tax", "shippingCost", "quantity", "url", "isTaxable"].includes(field) || field === "vendor") {
          updated.vendors = [{
            ...updated.vendors[0],
            [field]: parsedValue,
            ...(field === "vendor" && updated.isTaxable !== undefined ? { isTaxable: updated.isTaxable, tax: updated.tax } : {})
          }];
        }
      }

      return updated;
    });

    let updatedItems = rawUpdatedItems;
    if (["quantity", "vendorPrice", "shippingCost", "tax", "vendor"].includes(field)) {
      updatedItems = recalculateAllPoItemClientPrices(rawUpdatedItems, item => getItemMarkup(item), defaultTaxRate);
    }

    setItems(updatedItems);
  };

  // Vendor split helpers
  const handleAddVendorSplit = (itemId) => {
    const rawUpdatedItems = items.map(item => {
      if (item.id !== itemId) return item;

      const existingVendors = item.vendors && item.vendors.length > 0 ? item.vendors : [
        { id: 'default', vendor: item.vendor || "", quantity: item.quantity || 1, vendorPrice: item.vendorPrice || 0, isTaxable: item.isTaxable !== false, tax: item.tax, shippingCost: item.shippingCost || 0, url: item.url || "" }
      ];

      const newVendor = {
        id: `v-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
        vendor: "",
        quantity: 1,
        vendorPrice: 0,
        isTaxable: true,
        tax: true,
        shippingCost: 0,
        url: ""
      };

      const updatedItem = {
        ...item,
        vendors: [...existingVendors, newVendor],
        vendor: existingVendors[0]?.vendor || item.vendor || "",
        vendorPrice: 0,
        isTaxable: true,
        tax: true,
        shippingCost: 0,
        url: ""
      };
      updatedItem.quantity = updatedItem.vendors.reduce((sum, v) => sum + Number(v.quantity || 0), 0);
      return updatedItem;
    });

    const updatedItems = recalculateAllPoItemClientPrices(rawUpdatedItems, item => getItemMarkup(item), getDefaultTaxRate());
    setItems(updatedItems);
  };

  const handleRemoveVendorSplit = (itemId, vendorId) => {
    const rawUpdatedItems = items.map(item => {
      if (item.id !== itemId) return item;

      const existingVendors = item.vendors && item.vendors.length > 0 ? item.vendors : [
        { id: 'default', vendor: item.vendor || "", quantity: item.quantity || 1, vendorPrice: item.vendorPrice || 0, isTaxable: item.isTaxable !== false, tax: item.tax, shippingCost: item.shippingCost || 0, url: item.url || "" }
      ];

      const updatedVendors = existingVendors.filter(v => v.id !== vendorId);

      const isSingle = updatedVendors.length === 1;
      const updatedItem = {
        ...item,
        vendors: updatedVendors,
        vendor: isSingle ? updatedVendors[0].vendor : (updatedVendors[0]?.vendor || item.vendor),
        vendorPrice: isSingle ? updatedVendors[0].vendorPrice : item.vendorPrice,
        isTaxable: isSingle ? updatedVendors[0].isTaxable : item.isTaxable,
        tax: isSingle ? updatedVendors[0].tax : item.tax,
        shippingCost: isSingle ? updatedVendors[0].shippingCost : item.shippingCost,
        url: isSingle ? updatedVendors[0].url : item.url
      };
      updatedItem.quantity = updatedVendors.reduce((sum, v) => sum + Number(v.quantity || 0), 0);
      return updatedItem;
    });

    const updatedItems = recalculateAllPoItemClientPrices(rawUpdatedItems, item => getItemMarkup(item), getDefaultTaxRate());
    setItems(updatedItems);
  };

  const handleUpdateVendorField = (itemId, vendorId, field, value) => {
    const defaultTaxRate = getDefaultTaxRate();
    const rawUpdatedItems = items.map(item => {
      if (item.id !== itemId) return item;

      const existingVendors = item.vendors && item.vendors.length > 0 ? item.vendors : [
        { id: 'default', vendor: item.vendor || "", quantity: item.quantity || 1, vendorPrice: item.vendorPrice || 0, isTaxable: item.isTaxable !== false, tax: item.tax, shippingCost: item.shippingCost || 0, url: item.url || "" }
      ];

      const updatedVendors = existingVendors.map(v => {
        if (v.id !== vendorId) return v;

        let parsedValue = value;
        if (field === "quantity") {
          parsedValue = value === "" ? "" : Number(value);
        }
        
        let updatedSplit = { ...v, [field]: parsedValue };
        
        // Auto-update tax checkbox based on vendor tax exemption status
        if (field === "vendor" && value && value.trim()) {
          const normVal = value.trim().toLowerCase();
          const matchedVendor = (vendors || []).find(vend => (vend.name || "").trim().toLowerCase() === normVal);
          if (matchedVendor) {
            const isExempt = String(matchedVendor.taxExempt).toLowerCase() === "yes" || matchedVendor.taxExempt === true;
            if (isExempt) {
              updatedSplit.isTaxable = false;
              updatedSplit.tax = 0;
            } else {
              updatedSplit.isTaxable = true;
              updatedSplit.tax = defaultTaxRate;
            }
          } else {
            const autofilledTax = findExistingVendorTax(value);
            if (autofilledTax !== null && (!v.tax || v.tax === "0.00" || v.tax === "0" || v.tax === 0)) {
              updatedSplit.tax = autofilledTax;
            }
          }
        }

        return updatedSplit;
      });

      const isSingle = updatedVendors.length === 1;
      const topVendor = isSingle ? updatedVendors[0].vendor : (item.vendor || updatedVendors[0].vendor);
      const topTax = isSingle ? updatedVendors[0].tax : (item.tax || updatedVendors[0].tax);
      const topIsTaxable = isSingle ? updatedVendors[0].isTaxable : (item.isTaxable !== undefined ? item.isTaxable : updatedVendors[0].isTaxable);
      const topUrl = isSingle ? updatedVendors[0].url : (item.url || updatedVendors[0].url);
      const topVendorPrice = isSingle ? updatedVendors[0].vendorPrice : item.vendorPrice;
      const topShippingCost = isSingle ? updatedVendors[0].shippingCost : item.shippingCost;

      const updatedItem = {
        ...item,
        vendor: topVendor,
        tax: topTax,
        isTaxable: topIsTaxable,
        url: topUrl,
        vendorPrice: topVendorPrice,
        shippingCost: topShippingCost,
        vendors: updatedVendors
      };
      updatedItem.quantity = updatedVendors.reduce((sum, v) => sum + Number(v.quantity || 0), 0);
      return updatedItem;
    });

    const updatedItems = recalculateAllPoItemClientPrices(rawUpdatedItems, item => getItemMarkup(item), defaultTaxRate);
    setItems(updatedItems);
  };

  const handleToggleItemTax = (itemId, vendorId, checked) => {
    const defaultTaxRate = getDefaultTaxRate();
    const rawUpdatedItems = items.map(item => {
      if (item.id !== itemId) return item;

      const existingVendors = item.vendors && item.vendors.length > 0 ? item.vendors : [
        { id: 'default', vendor: item.vendor || "", quantity: item.quantity || 1, vendorPrice: item.vendorPrice || 0, isTaxable: item.isTaxable !== false, tax: item.tax, shippingCost: item.shippingCost || 0, url: item.url || "" }
      ];

      const updatedVendors = existingVendors.map(v => {
        if (v.id !== vendorId && !(vendorId === 'default' && (!v.id || v.id === 'default'))) return v;
        return {
          ...v,
          isTaxable: checked,
          tax: checked ? defaultTaxRate : 0
        };
      });

      const topIsTaxable = updatedVendors.length === 1 ? updatedVendors[0].isTaxable : item.isTaxable;
      const topTax = updatedVendors.length === 1 ? updatedVendors[0].tax : (checked ? defaultTaxRate : 0);

      const updatedItem = {
        ...item,
        isTaxable: topIsTaxable !== undefined ? topIsTaxable : checked,
        tax: topTax,
        vendors: updatedVendors
      };
      return updatedItem;
    });

    const updatedItems = recalculateAllPoItemClientPrices(rawUpdatedItems, item => getItemMarkup(item), defaultTaxRate);
    setItems(updatedItems);
  };

  const handleGlobalFactorsChange = (newShipping, newMarkup) => {
    const baseMarkup = newMarkup !== undefined ? parseFloat(newMarkup || 0) : getPoMarkupRate();
    const effectiveMarkup = isCcPayment() ? (baseMarkup + getPoCcFee()) : baseMarkup;
    const shippingOut = newShipping !== undefined ? Number(newShipping || 0) : Number(estShippingCost || 0);

    const updatedItems = recalculateAllPoItemClientPrices(items, item => {
      if (item && item.isMarkupCustom && item.markupPercentage !== undefined && item.markupPercentage !== null) {
        return Number(item.markupPercentage);
      }
      return effectiveMarkup;
    }, getDefaultTaxRate(), shippingOut);
    setItems(updatedItems);
  };

  const handleGeneratePDF = () => {
    let activeLogoUrl = po.logoUrl || "";
    if (!activeLogoUrl) {
      const savedLogos = localStorage.getItem("global_company_logos");
      if (savedLogos) {
        try {
          const parsed = JSON.parse(savedLogos);
          const active = parsed.find(l => l.isActive);
          if (active) activeLogoUrl = active.url;
        } catch (e) {
          console.error("Failed to parse logos for PDF:", e);
        }
      }
    }
    
    const getEffectiveRate = () => {
      const isNonCc = po && po.paymentMethod && po.paymentMethod.trim() !== "" && po.paymentMethod.toLowerCase() !== "credit card";
      if (isNonCc) {
        const savedDiscount = localStorage.getItem("global_non_cc_discount");
        const discount = savedDiscount !== null ? parseFloat(savedDiscount) : 3;
        return Math.max(0, Number(markupRate) - discount);
      }
      return Number(markupRate);
    };

    const effectiveRate = getEffectiveRate();
    const estShipping = Number(estShippingCost || 0);

    const getItemMarkup = (item) => {
      if (item && item.isMarkupCustom && item.markupPercentage !== undefined && item.markupPercentage !== null) {
        return Number(item.markupPercentage);
      }
      return effectiveRate;
    };

    const multipliers = calculateVendorShippingMultipliers(items);
    const totalCost = calculateTotalCostSum(items, null, multipliers);
    const newDecimal = calculateNewDecimal(estShipping, totalCost);
    const clientPriceSum = items ? items.reduce((sum, item) => {
      const price = (item.unitPrice !== undefined && item.unitPrice !== null && item.unitPrice !== "")
        ? Number(item.unitPrice)
        : recalculateItemClientPrice(item, getItemMarkup(item), multipliers, null, newDecimal);
      return sum + (isNaN(price) ? 0 : price);
    }, 0) : 0;
    
    const styles = `
      body {
        font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
        color: #2c3e50;
        margin: 0;
        padding: 0;
        line-height: 1.5;
        background-color: #f8fafc;
      }
      .content-wrapper {
        background-color: #ffffff;
        max-width: 900px;
        margin: 30px auto;
        padding: 40px;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
        border-radius: 8px;
      }
      .header-table {
        width: 100%;
        border-collapse: collapse;
        margin-bottom: 40px;
      }
      .header-table td {
        border: none;
        padding: 0;
        vertical-align: top;
      }
      .title {
        font-size: 26px;
        font-weight: 800;
        color: #2980b9;
        letter-spacing: 0.5px;
        text-transform: uppercase;
        margin-bottom: 10px;
      }
      .meta-info {
        font-size: 14px;
        color: #7f8c8d;
      }
      .meta-info strong {
        color: #2c3e50;
      }
      .client-info {
        text-align: right;
      }
      .client-label {
        font-size: 11px;
        text-transform: uppercase;
        color: #95a5a6;
        font-weight: 700;
        letter-spacing: 1px;
      }
      .client-name {
        font-size: 20px;
        font-weight: 700;
        color: #2c3e50;
        margin-top: 5px;
      }
      .section-title {
        font-size: 12px;
        text-transform: uppercase;
        color: #95a5a6;
        font-weight: 700;
        letter-spacing: 1px;
        border-bottom: 2px solid #bdc3c7;
        padding-bottom: 5px;
        margin-bottom: 10px;
      }
      .address-box {
        margin-bottom: 40px;
        font-size: 14px;
        color: #34495e;
      }
      table.items-table {
        width: 100%;
        border-collapse: collapse;
        margin-bottom: 40px;
      }
      table.items-table th {
        background-color: #f8f9fa;
        border-bottom: 2px solid #bdc3c7;
        padding: 12px 10px;
        font-size: 13px;
        font-weight: 700;
        color: #34495e;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
      table.items-table td {
        border-bottom: 1px solid #ecf0f1;
        padding: 15px 10px;
        font-size: 14px;
        color: #2c3e50;
      }
      .text-right {
        text-align: right;
      }
      .text-center {
        text-align: center;
      }
      .totals-container {
        display: flex;
        justify-content: flex-end;
        margin-top: 20px;
      }
      .totals-box {
        border-top: 2px solid #2980b9;
        padding-top: 15px;
        text-align: right;
        min-width: 250px;
      }
      .totals-label {
        font-size: 12px;
        text-transform: uppercase;
        color: #7f8c8d;
        font-weight: 700;
      }
      .totals-value {
        font-size: 22px;
        font-weight: 800;
        color: #2980b9;
        margin-top: 5px;
      }
      @media print {
        body {
          background-color: #ffffff;
          padding: 0;
        }
        .content-wrapper {
          box-shadow: none;
          margin: 0;
          padding: 15px;
          border-radius: 0;
          max-width: 100%;
        }
      }
    `;

    const bodyHtml = `
      <div class="content-wrapper">
        <table class="header-table">
            <tr>
              <td>
                <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 15px;">
                  ${activeLogoUrl ? `<img src="${activeLogoUrl}" style="width: 54px; height: 54px; border-radius: 6px; object-fit: cover;" />` : ""}
                  <div class="title" style="margin: 0;">Bid Proposal Quote</div>
                </div>
                <div class="meta-info">
                  Proposal Number: <strong>${poNumber}</strong><br/>
                  Project Name: <strong>${title}</strong><br/>
                  Date Received from Customer: <strong>${receivedFromCustomerDate || po.receivedFromCustomerDate || po.date || "N/A"}</strong><br/>
                  Date Estimate Submitted: <strong>${po.submittedDate || ((po.status === "Bid Submitted" || po.status === "Won" || po.status === "Lost") ? po.date : "Pending Submission")}</strong><br/>
                  Estimate Expiration Date: <strong>${expirationDate || po.expirationDate || (po.dueDate || "30 Days from Submission")}</strong><br/>
                </div>
              </td>
              <td class="client-info">
                <span class="client-label">Client Partner</span>
                <div class="client-name">${po.clientName}</div>
              </td>
            </tr>
          </table>
          
          <div class="section-title">Ship To Address</div>
          <div class="address-box">
            ${po.shipToAddress || "N/A"}<br/>
            ${po.shipToAddress2 ? po.shipToAddress2 + "<br/>" : ""}
            ${po.shipToCity || ""}, ${po.shipToState || ""} ${po.shipToZip || ""}
            ${po.markFor ? `<div style="margin-top: 6px; color: #2980b9;"><strong>Mark For (M/F):</strong> ${po.markFor}</div>` : ""}
          </div>
          
          <div class="section-title">Quote Specifications</div>
          <table class="items-table">
            <thead>
              <tr>
                <th class="text-center" style="width: 60px;">Line #</th>
                <th>Description</th>
                <th class="text-center" style="width: 80px;">Qty</th>
                <th class="text-right" style="width: 130px;">Client Price</th>
                <th class="text-right" style="width: 150px;">Total Price</th>
              </tr>
            </thead>
            <tbody>
              ${items && items.length > 0 ? items.map((item, idx) => {
                const itemVendors = item.vendors && item.vendors.length > 0 ? item.vendors : [
                  { quantity: item.quantity, vendorPrice: item.vendorPrice, shippingCost: item.shippingCost }
                ];
                const qty = itemVendors.reduce((qSum, v) => qSum + Number(v.quantity || 0), 0);
                const totalLineClientPrice = (item.unitPrice !== undefined && item.unitPrice !== null && item.unitPrice !== "")
                  ? Number(item.unitPrice)
                  : recalculateItemClientPrice(item, getItemMarkup(item), multipliers);
                const unitClientPrice = qty > 0 ? totalLineClientPrice / qty : totalLineClientPrice;
                const lineNum = item.lineNumber || String(idx + 1).padStart(2, "0");
                return `
                  <tr>
                    <td class="text-center" style="font-weight: 600; color: #7f8c8d;">${lineNum}</td>
                    <td>${item.description || "N/A"}</td>
                    <td class="text-center">${qty}</td>
                    <td class="text-right">$${unitClientPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td class="text-right">$${totalLineClientPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  </tr>
                `;
              }).join("") : `
                <tr>
                  <td colspan="5" class="text-center" style="color: #95a5a6;">No specifications listed</td>
                </tr>
              `}
            </tbody>
          </table>
          
          <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-top: 30px;">
            <div style="text-align: left; max-width: 60%; padding-right: 20px;">
              ${po.additionalInfo ? `
                <div style="font-size: 11px; text-transform: uppercase; color: #7f8c8d; font-weight: 700; margin-bottom: 5px;">Note to Customer:</div>
                <div style="font-size: 13px; color: #2c3e50; line-height: 1.6; white-space: pre-wrap;">${po.additionalInfo}</div>
              ` : ""}
            </div>
            <div class="totals-box" style="border-top: 2px solid #2980b9; padding-top: 15px; text-align: right; min-width: 250px;">
              <span class="totals-label">Total Client Price</span>
              <div class="totals-value">$${clientPriceSum.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
          </div>
        </div>
      </div>
    `;

    openDocumentPrintWindow({
      title: `${poNumber} - Bid Proposal`,
      headStyles: styles,
      bodyHtml
    });
  };

  // Save and compare changes
  const handleSave = async () => {
    if (!poNumber.trim()) {
      setError("EST Number cannot be empty.");
      return;
    }
    setError("");
    setSuccess("");
    setIsSaving(true);

    try {
      const changes = [];

      const defaultTaxRate = getDefaultTaxRate();

      // Clean items values
      const rawCleanedItems = items.map(item => {
        const cleanedVendors = (item.vendors || []).map(v => {
          const isTaxable = v.isTaxable !== false && v.tax !== false && v.tax !== "false";
          return {
            ...v,
            quantity: v.quantity === "" ? 0 : Number(v.quantity || 0),
            vendorPrice: v.vendorPrice === "" || isNaN(Number(v.vendorPrice)) ? "0.00" : Number(v.vendorPrice).toFixed(2),
            isTaxable: isTaxable,
            tax: isTaxable ? defaultTaxRate : 0,
            shippingCost: v.shippingCost === "" || isNaN(Number(v.shippingCost)) ? "0.00" : Number(v.shippingCost).toFixed(2)
          };
        });

        let vPrice = item.vendorPrice === "" || isNaN(Number(item.vendorPrice)) ? "0.00" : Number(item.vendorPrice).toFixed(2);
        let sCost = item.shippingCost === "" || isNaN(Number(item.shippingCost)) ? "0.00" : Number(item.shippingCost).toFixed(2);
        const itemIsTaxable = item.isTaxable !== false && item.tax !== false && item.tax !== "false";
        let taxVal = itemIsTaxable ? defaultTaxRate : 0;
        let isTaxableVal = itemIsTaxable;
        let qty = item.quantity === "" ? 0 : Number(item.quantity || 0);
        let vendorName = item.vendor || "";
        let urlVal = item.url || "";

        if (cleanedVendors.length === 1) {
          vPrice = cleanedVendors[0].vendorPrice;
          sCost = cleanedVendors[0].shippingCost;
          taxVal = cleanedVendors[0].tax;
          isTaxableVal = cleanedVendors[0].isTaxable;
          qty = cleanedVendors[0].quantity;
          vendorName = cleanedVendors[0].vendor || "";
          urlVal = cleanedVendors[0].url || "";
        } else if (cleanedVendors.length > 1) {
          qty = cleanedVendors.reduce((sum, v) => sum + Number(v.quantity || 0), 0);
          vendorName = cleanedVendors[0].vendor || item.vendor || "";
        }

        return {
          ...item,
          quantity: qty,
          vendor: vendorName,
          vendorPrice: vPrice,
          isTaxable: isTaxableVal,
          tax: taxVal,
          shippingCost: sCost,
          url: urlVal,
          ...(cleanedVendors.length > 0 ? { vendors: cleanedVendors } : {})
        };
      });

      const cleanedItems = recalculateAllPoItemClientPrices(rawCleanedItems, item => getItemMarkup(item), defaultTaxRate);

      // Basic properties change detection
      if (poNumber.trim() !== (po.poNumber || "").trim()) {
        changes.push(`EST Number changed from "${po.poNumber || ""}" to "${poNumber}"`);
      }
      if ((customerPoNumber || "").trim() !== (po.customerPoNumber || "").trim()) {
        changes.push(`Customer PO # changed from "${po.customerPoNumber || ""}" to "${customerPoNumber.trim()}"`);
      }
      if (title.trim() !== (po.title || "").trim()) {
        changes.push(`Project Title changed from "${po.title || ""}" to "${title}"`);
      }
      if ((receivedFromCustomerDate || "").trim() !== (po.receivedFromCustomerDate || "").trim()) {
        changes.push(`Received from Customer Date changed from "${po.receivedFromCustomerDate || ""}" to "${(receivedFromCustomerDate || "").trim()}"`);
      }
      if ((expirationDate || "").trim() !== (po.expirationDate || "").trim()) {
        changes.push(`Estimate Expiration Date changed from "${po.expirationDate || ""}" to "${(expirationDate || "").trim()}"`);
      }
      if (Number(estShippingCost || 0) !== Number(po.estShippingCost || 0)) {
        changes.push(`Est. Shipping Cost changed from $${Number(po.estShippingCost || 0).toFixed(2)} to $${Number(estShippingCost || 0).toFixed(2)}`);
      }
      if (Number(markupRate || 0) !== Number(po.markupRate || 0)) {
        changes.push(`Markup Rate changed from ${po.markupRate || 0}% to ${markupRate || 0}%`);
      }
      if (Boolean(isPriority) !== Boolean(po.isPriority)) {
        changes.push(`Priority changed to ${isPriority ? "Priority" : "Normal"}`);
      }

      // Items change detection
      const originalItems = po.items || [];
      const originalItemsMap = new Map(originalItems.map(i => [i.id, i]));
      const currentItemsMap = new Map(cleanedItems.map(i => [i.id, i]));

      // 1. Added
      cleanedItems.forEach(item => {
        if (!originalItemsMap.has(item.id)) {
          changes.push(`Added Line Item ${item.lineNumber || ""}: "${item.description || "No description"}" (Qty: ${item.quantity}, Cost: $${parseFloat(item.vendorPrice || 0).toFixed(2)})`);
        }
      });

      // 2. Deleted
      originalItems.forEach(item => {
        if (!currentItemsMap.has(item.id)) {
          changes.push(`Deleted Line Item ${item.lineNumber || ""}: "${item.description || "No description"}"`);
        }
      });

      // 3. Modified
      cleanedItems.forEach(item => {
        const orig = originalItemsMap.get(item.id);
        if (orig) {
          const itemChanges = [];
          if (item.lineNumber !== orig.lineNumber) {
            itemChanges.push(`Line # "${orig.lineNumber}" -> "${item.lineNumber}"`);
          }
          if ((item.description || "").trim() !== (orig.description || "").trim()) {
            itemChanges.push(`Description "${orig.description || ""}" -> "${item.description || ""}"`);
          }
          if (Number(item.quantity) !== Number(orig.quantity)) {
            itemChanges.push(`Qty ${orig.quantity} -> ${item.quantity}`);
          }
          if ((item.vendor || "").trim() !== (orig.vendor || "").trim()) {
            itemChanges.push(`Vendor "${orig.vendor || ""}" -> "${item.vendor || ""}"`);
          }
          if (Number(item.vendorPrice) !== Number(orig.vendorPrice)) {
            itemChanges.push(`Vendor Price $${Number(orig.vendorPrice).toFixed(2)} -> $${Number(item.vendorPrice).toFixed(2)}`);
          }
          const wasTaxable = orig.isTaxable !== false && orig.tax !== false && orig.tax !== "false";
          const isTaxable = item.isTaxable !== false && item.tax !== false && item.tax !== "false";
          if (wasTaxable !== isTaxable) {
            itemChanges.push(`Tax ${wasTaxable ? "Taxable" : "Non-taxable"} -> ${isTaxable ? "Taxable" : "Non-taxable"}`);
          }
          if (Number(item.shippingCost) !== Number(orig.shippingCost)) {
            itemChanges.push(`Shipping Cost $${Number(orig.shippingCost).toFixed(2)} -> $${Number(item.shippingCost).toFixed(2)}`);
          }
          if (item.staff !== orig.staff) {
            itemChanges.push(`Staff "${orig.staff || ""}" -> "${item.staff || ""}"`);
          }
          if (item.url !== orig.url) {
            itemChanges.push(`URL "${orig.url || ""}" -> "${item.url || ""}"`);
          }

          // Vendor splits differences
          const origSplits = orig.vendors || [];
          const currSplits = item.vendors || [];
          if (origSplits.length !== currSplits.length) {
            itemChanges.push(`Vendor splits count changed (${origSplits.length} -> ${currSplits.length})`);
          } else {
            currSplits.forEach((cs, sIdx) => {
              const os = origSplits[sIdx];
              if (os) {
                if (Number(cs.quantity) !== Number(os.quantity)) itemChanges.push(`Split Qty ${os.quantity} -> ${cs.quantity}`);
                if (Number(cs.vendorPrice) !== Number(os.vendorPrice)) itemChanges.push(`Split Price $${Number(os.vendorPrice).toFixed(2)} -> $${Number(cs.vendorPrice).toFixed(2)}`);
                const sWasTaxable = os.isTaxable !== false && os.tax !== false && os.tax !== "false";
                const sIsTaxable = cs.isTaxable !== false && cs.tax !== false && cs.tax !== "false";
                if (sWasTaxable !== sIsTaxable) itemChanges.push(`Split Tax ${sWasTaxable ? "Taxable" : "Non-taxable"} -> ${sIsTaxable ? "Taxable" : "Non-taxable"}`);
                if (Number(cs.shippingCost) !== Number(os.shippingCost)) itemChanges.push(`Split Shipping $${Number(os.shippingCost).toFixed(2)} -> $${Number(cs.shippingCost).toFixed(2)}`);
                if ((cs.vendor || "").trim() !== (os.vendor || "").trim()) itemChanges.push(`Split Vendor "${os.vendor || ""}" -> "${cs.vendor || ""}"`);
              }
            });
          }

          if (itemChanges.length > 0) {
            changes.push(`Modified Line ${item.lineNumber || ""}: ${itemChanges.join(", ")}`);
          }
        }
      });

      if (changes.length === 0) {
        setError("No changes detected. Press cancel to close.");
        setIsSaving(false);
        return;
      }

      // Build history entry
      const historyEntry = {
        timestamp: new Date().toISOString(),
        user: currentUser?.email || "System",
        changes: changes
      };

      // Save to PO doc in Firestore
      const poRef = doc(db, "pos", po.id);
      await updateDoc(poRef, {
        title: title,
        poNumber: poNumber.trim(),
        customerPoNumber: customerPoNumber.trim(),
        receivedFromCustomerDate: (receivedFromCustomerDate || "").trim(),
        expirationDate: (expirationDate || "").trim(),
        estShippingCost: Number(estShippingCost || 0),
        markupRate: Number(markupRate || 0),
        isPriority: Boolean(isPriority),
        items: cleanedItems,
        changeHistory: arrayUnion(historyEntry),
        updatedAt: new Date().toISOString()
      });

      // Update local po object reference
      po.title = title;
      po.poNumber = poNumber.trim();
      po.customerPoNumber = customerPoNumber.trim();
      po.receivedFromCustomerDate = (receivedFromCustomerDate || "").trim();
      po.expirationDate = (expirationDate || "").trim();
      po.estShippingCost = Number(estShippingCost || 0);
      po.markupRate = Number(markupRate || 0);
      po.isPriority = Boolean(isPriority);
      po.items = cleanedItems;

      // Sync items to Logistics Shipment
      const logisticsCol = collection(db, "logistics");
      const q = query(logisticsCol, where("poId", "==", po.id));
      const qSnapshot = await getDocs(q);
      
      if (!qSnapshot.empty) {
        const shipmentDoc = qSnapshot.docs[0];
        const shipmentData = shipmentDoc.data();
        const updatedItemStatuses = { ...(shipmentData.itemStatuses || {}) };

        // 1. Add new items status
        cleanedItems.forEach(item => {
          if (!originalItemsMap.has(item.id)) {
            updatedItemStatuses[item.id] = {
              status: "To Order",
              orderedDate: ""
            };
          }
        });

        // 2. Remove deleted items status
        originalItems.forEach(item => {
          if (!currentItemsMap.has(item.id)) {
            delete updatedItemStatuses[item.id];
          }
        });

        // Save updated statuses & updated poNumber / customerPoNumber to shipment doc
        await updateDoc(doc(db, "logistics", shipmentDoc.id), {
          itemStatuses: updatedItemStatuses,
          poNumber: poNumber.trim(),
          customerPoNumber: customerPoNumber.trim()
        });
      }

      // Sync poNumber to Chat channels and Inventory
      if (poNumber.trim() !== (po.poNumber || "").trim()) {
        const chatsCol = collection(db, "chats");
        const chatsQ = query(chatsCol, where("poId", "==", po.id));
        const chatsSnapshot = await getDocs(chatsQ);
        if (!chatsSnapshot.empty) {
          const clientName = po.customerName ? po.customerName.toLowerCase().replace(/[^a-z0-9]/g, "-") : 
                             (po.clientName ? po.clientName.toLowerCase().replace(/[^a-z0-9]/g, "-") : "client");
          const newChannelName = `po-${poNumber.trim().toLowerCase()}-${clientName}`;
          for (const chatDoc of chatsSnapshot.docs) {
            await updateDoc(doc(db, "chats", chatDoc.id), {
              name: newChannelName
            });
          }
        }

        const inventoryCol = collection(db, "inventory");
        const invQ = query(inventoryCol, where("poId", "==", po.id));
        const invSnapshot = await getDocs(invQ);
        for (const invDoc of invSnapshot.docs) {
          await updateDoc(doc(db, "inventory", invDoc.id), {
            poNumber: poNumber.trim()
          });
        }
      }

      setSuccess("Changes saved successfully!");
      setTimeout(() => {
        onClose();
      }, 1200);
    } catch (err) {
      console.error("Error saving Change Request modifications:", err);
      setError(err.message || "Failed to save changes. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="modal-overlay" style={{ display: "flex", zIndex: 1100 }}>
      <div className="modal-container" style={{ width: "95vw", maxWidth: "1200px", maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
        
        {/* Modal Header */}
        <div className="modal-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h3 className="modal-title" style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              EST Change Request Modal: <span style={{ color: "var(--text-primary)" }}>{po.poNumber}</span>
            </h3>
            <span style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>
              Client: <strong>{po.clientName}</strong>
            </span>
          </div>
          <button className="modal-close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="modal-body" style={{ overflowY: "auto", flex: 1, padding: "1.5rem" }}>
          {error && (
            <div className="alert alert-danger" style={{ marginBottom: "1rem" }}>
              {error}
            </div>
          )}
          {success && (
            <div className="alert alert-success" style={{ marginBottom: "1rem", backgroundColor: "rgba(46, 204, 113, 0.1)", border: "1px solid var(--success)", color: "var(--success)", padding: "0.75rem 1rem", borderRadius: "4px" }}>
              {success}
            </div>
          )}

          {/* Form Header info */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "1rem", marginBottom: "1.5rem", alignItems: "flex-end" }}>
            <div className="form-group">
              <label className="form-label" htmlFor="cr-po-number">EST Number *</label>
              <input
                id="cr-po-number"
                type="text"
                className="form-input"
                value={poNumber}
                onChange={(e) => setPoNumber(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="cr-cust-po">Customer PO #</label>
              <input
                id="cr-cust-po"
                type="text"
                placeholder="e.g. CUST-PO-1234"
                className="form-input"
                value={customerPoNumber}
                onChange={(e) => setCustomerPoNumber(e.target.value)}
              />
            </div>
            <div className="form-group" style={{ minWidth: "220px" }}>
              <label className="form-label" htmlFor="cr-title">Project Name / Title</label>
              <input
                id="cr-title"
                type="text"
                className="form-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="cr-received-customer-date">Received from Customer</label>
              <input
                id="cr-received-customer-date"
                type="date"
                className="form-input"
                value={receivedFromCustomerDate}
                onChange={(e) => setReceivedFromCustomerDate(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="cr-expiration-date">Estimate Expiration Date</label>
              <input
                id="cr-expiration-date"
                type="date"
                className="form-input"
                value={expirationDate}
                onChange={(e) => setExpirationDate(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="cr-shipping">Shipping Cost Out ($)</label>
              <input
                id="cr-shipping"
                type="number"
                step="any"
                className="form-input"
                value={estShippingCost}
                onChange={(e) => {
                  const raw = e.target.value;
                  setEstShippingCost(raw);
                  const val = raw === "" ? 0 : Number(raw);
                  handleGlobalFactorsChange(val, markupRate);
                }}
                onFocus={(e) => e.target.select()}
                onClick={(e) => e.target.select()}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="cr-markup">Markup Rate (%)</label>
              <input
                id="cr-markup"
                type="number"
                step="any"
                className="form-input"
                value={markupRate}
                onChange={(e) => {
                  const raw = e.target.value;
                  setMarkupRate(raw);
                  const val = raw === "" ? 0 : Number(raw);
                  handleGlobalFactorsChange(estShippingCost, val);
                }}
                onFocus={(e) => e.target.select()}
                onClick={(e) => e.target.select()}
              />
            </div>
            <div className="form-group" style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end", height: "100%", paddingBottom: "0.25rem" }}>
              <label style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", cursor: "pointer", userSelect: "none", fontSize: "0.9rem", fontWeight: 600, color: isPriority ? "#f59e0b" : "var(--text-secondary)" }}>
                <input
                  type="checkbox"
                  checked={isPriority}
                  onChange={(e) => setIsPriority(e.target.checked)}
                  style={{ width: "16px", height: "16px", accentColor: "#f59e0b", cursor: "pointer" }}
                />
                Priority
              </label>
            </div>
          </div>

          {/* Spreadsheet Table */}
          <div className="table-container" style={{ border: "1px solid var(--border-color)", borderRadius: "6px", overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
              <thead>
                <tr style={{ backgroundColor: "rgba(255, 255, 255, 0.02)", borderBottom: "1px solid var(--border-color)" }}>
                  <th style={{ padding: "0.5rem", width: "50px", textAlign: "center" }}>Line</th>
                  <th style={{ padding: "0.5rem", minWidth: "200px" }}>Description</th>
                  <th style={{ padding: "0.5rem", width: "70px", textAlign: "center" }}>Qty</th>
                  <th style={{ padding: "0.5rem", width: "110px", textAlign: "right" }}>Client Price (Unit)</th>
                  <th style={{ padding: "0.5rem", width: "70px", textAlign: "center" }}>%</th>
                  <th style={{ padding: "0.5rem", width: "120px" }}>Vendor</th>
                  <th style={{ padding: "0.5rem", width: "36px", textAlign: "center" }}></th>
                  <th style={{ padding: "0.5rem", width: "90px", textAlign: "right" }}>Unit Price</th>
                  <th style={{ padding: "0.5rem", width: "60px", textAlign: "center" }}>Tax</th>
                  <th style={{ padding: "0.5rem", width: "90px", textAlign: "right" }} title="Vendor Shipping Cost">VSC</th>
                  <th style={{ padding: "0.5rem", width: "100px" }}>Staff</th>
                  <th style={{ padding: "0.5rem", width: "70px", textAlign: "center" }}></th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan="12" style={{ textAlign: "center", padding: "2rem", color: "var(--text-muted)" }}>
                      No items recorded. Click "Add Line Item" below to start.
                    </td>
                  </tr>
                ) : (
                  items.map((item, idx) => {
                    const itemVendors = item.vendors && item.vendors.length > 0 ? item.vendors : [
                      { id: 'default', vendor: item.vendor || "", quantity: item.quantity || 0, vendorPrice: item.vendorPrice || 0, shippingCost: item.shippingCost || 0, url: item.url || "" }
                    ];
                    const isDuplicateSplit = idx > 0 && item.lineNumber && items.slice(0, idx).some(prev => prev.lineNumber === item.lineNumber);
                    return (
                    <tr key={item.id} style={{ borderBottom: "1px solid var(--border-color)", opacity: isDuplicateSplit ? 0.75 : 1 }}>
                      <td style={{ padding: "0.25rem", textAlign: "center" }}>
                        <input
                          type="text"
                          className="inline-edit-input text-center"
                          value={item.lineNumber || ""}
                          onChange={(e) => handleUpdateItemField(item.id, "lineNumber", e.target.value)}
                          onFocus={(e) => e.target.select()}
                          onClick={(e) => e.target.select()}
                          style={{ width: "40px" }}
                          disabled={isDuplicateSplit}
                        />
                      </td>
                      <td style={{ padding: "0.25rem" }}>
                        <input
                          type="text"
                          className="inline-edit-input"
                          value={item.description || ""}
                          onChange={(e) => handleUpdateItemField(item.id, "description", e.target.value)}
                          placeholder="e.g. Industrial Fasteners"
                          disabled={isDuplicateSplit}
                        />
                      </td>
                      <td style={{ padding: "0.25rem", textAlign: "center" }}>
                         <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                          {itemVendors.map((v, vIdx) => (
                            <div key={v.id} style={{ display: "flex", alignItems: "center", height: "32px" }}>
                              <input
                                type="number"
                                className="inline-edit-input text-center"
                                value={v.quantity}
                                onChange={(e) => handleUpdateVendorField(item.id, v.id, "quantity", e.target.value)}
                                onFocus={(e) => e.target.select()}
                                onClick={(e) => e.target.select()}
                                style={{ width: "60px" }}
                                disabled={isDuplicateSplit}
                              />
                            </div>
                          ))}
                        </div>
                      </td>
                      <td style={{ padding: "0.25rem", textAlign: "right", color: "var(--primary)", fontWeight: "600", verticalAlign: "top" }}>
                        <div style={{ height: "32px", display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                          ${item.unitPrice || "0.00"}
                        </div>
                      </td>
                      <td style={{ padding: "0.25rem", textAlign: "center", verticalAlign: "top" }}>
                        <div
                          onClick={() => {
                            if (!isDuplicateSplit) {
                              setSelectedMarkupItem(item);
                              setCustomMarkupModalOpen(true);
                            }
                          }}
                          style={{
                            height: "32px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: isDuplicateSplit ? "default" : "pointer",
                            fontWeight: item.isMarkupCustom ? "bold" : "normal",
                            color: item.isMarkupCustom ? "var(--success)" : "var(--text-secondary)",
                            borderRadius: "4px",
                            backgroundColor: item.isMarkupCustom ? "rgba(46, 204, 113, 0.05)" : "transparent"
                          }}
                          title={isDuplicateSplit ? "Splits inherit original markup and cannot be edited" : (item.isMarkupCustom ? "Custom markup set. Click to edit." : "Inherits EST markup. Click to customize.")}
                        >
                          {item.isMarkupCustom
                            ? (item.markupType === "dollar" && item.markupDollar !== undefined && item.markupDollar !== null
                                ? `$${Number(item.markupDollar).toFixed(2)}`
                                : `${item.markupPercentage}%`)
                            : `${markupRate}%`}
                        </div>
                      </td>
                      <td style={{ padding: "0.25rem" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                          {itemVendors.map((v, vIdx) => (
                            <div key={v.id} style={{ display: "flex", alignItems: "center", gap: "0.25rem", height: "32px" }}>
                              <input
                                type="text"
                                className="inline-edit-input"
                                value={v.vendor || ""}
                                onChange={(e) => handleUpdateVendorField(item.id, v.id, "vendor", e.target.value)}
                                placeholder="Vendor Name"
                                disabled={isDuplicateSplit}
                              />
                              {itemVendors.length > 1 && !isDuplicateSplit && (
                                <button
                                   type="button"
                                  onClick={() => handleRemoveVendorSplit(item.id, v.id)}
                                  style={{ background: "transparent", border: "none", color: "var(--danger)", cursor: "pointer", display: "flex", alignItems: "center", padding: "0 0.2rem" }}
                                  title="Remove split"
                                >
                                  <Trash2 size={12} />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </td>
                      <td style={{ padding: "0.25rem", textAlign: "center" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem", alignItems: "center" }}>
                          {itemVendors.map((v, vIdx) => (
                            <div key={v.id} style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "32px" }}>
                              {!isDuplicateSplit && (
                                <button
                                  type="button"
                                  className="btn btn-secondary btn-icon"
                                  onClick={() => handleAddVendorSplit(item.id)}
                                  title="Split Vendor"
                                  style={{
                                    width: "24px",
                                    height: "24px",
                                    minHeight: "auto",
                                    padding: 0,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    borderRadius: "4px",
                                    color: "var(--primary)",
                                    borderColor: "var(--border-color)",
                                    backgroundColor: "rgba(255, 255, 255, 0.03)"
                                  }}
                                >
                                  <Plus size={14} />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </td>
                      <td style={{ padding: "0.25rem" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                          {itemVendors.map((v, vIdx) => (
                            <div key={v.id} style={{ display: "flex", alignItems: "center", height: "32px" }}>
                              <input
                                type="text"
                                className="inline-edit-input text-right"
                                value={v.vendorPrice}
                                onChange={(e) => handleUpdateVendorField(item.id, v.id, "vendorPrice", e.target.value)}
                                onFocus={(e) => e.target.select()}
                                onClick={(e) => e.target.select()}
                                style={{ width: "80px" }}
                                disabled={isDuplicateSplit}
                              />
                            </div>
                          ))}
                        </div>
                      </td>
                      <td style={{ padding: "0.25rem" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                          {itemVendors.map((v) => {
                            const isChecked = v.isTaxable !== false && v.tax !== false && v.tax !== "false";
                            const globalTaxRate = getDefaultTaxRate();
                            return (
                              <div key={v.id} style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "32px" }}>
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={(e) => handleToggleItemTax(item.id, v.id, e.target.checked)}
                                  disabled={isDuplicateSplit}
                                  title={isChecked ? `Taxable (${globalTaxRate}%)` : "Non-taxable (0%)"}
                                  style={{
                                    width: "16px",
                                    height: "16px",
                                    cursor: isDuplicateSplit ? "not-allowed" : "pointer",
                                    accentColor: "var(--primary)",
                                    margin: 0
                                  }}
                                />
                              </div>
                            );
                          })}
                        </div>
                      </td>
                      <td style={{ padding: "0.25rem" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                          {itemVendors.map((v, vIdx) => (
                            <div key={v.id} style={{ display: "flex", alignItems: "center", height: "32px" }}>
                              <input
                                type="text"
                                className="inline-edit-input text-right"
                                value={v.shippingCost}
                                onChange={(e) => handleUpdateVendorField(item.id, v.id, "shippingCost", e.target.value)}
                                onFocus={(e) => e.target.select()}
                                onClick={(e) => e.target.select()}
                                style={{ width: "80px" }}
                                disabled={isDuplicateSplit}
                              />
                            </div>
                          ))}
                        </div>
                      </td>
                      <td style={{ padding: "0.25rem", verticalAlign: "top" }}>
                        <div style={{ height: "32px", display: "flex", alignItems: "center" }}>
                          {(() => {
                            const currentStaffVal = getMatchedStaffValue(item.staff);
                            const isCustomOrHistoricStaff = currentStaffVal && !users.some(u => u.value === currentStaffVal);
                            return (
                              <select
                                className="inline-edit-input"
                                value={currentStaffVal || ""}
                                onChange={(e) => handleUpdateItemField(item.id, "staff", e.target.value)}
                                disabled={isDuplicateSplit}
                                style={{
                                  backgroundColor: "var(--bg-input)",
                                  color: "var(--text-primary)",
                                  border: "none",
                                  borderRadius: "2px",
                                  padding: "0.15rem 0.25rem",
                                  width: "100%"
                                }}
                              >
                                <option value="">Select Staff</option>
                                {users.map(u => (
                                  <option key={u.value} value={u.value} style={{ backgroundColor: "var(--bg-card)" }}>
                                    {u.label}
                                  </option>
                                ))}
                                {isCustomOrHistoricStaff && (
                                  <option value={currentStaffVal} style={{ backgroundColor: "var(--bg-card)" }}>
                                    {item.staff}
                                  </option>
                                )}
                              </select>
                            );
                          })()}
                        </div>
                      </td>
                      <td style={{ padding: "0.25rem", textAlign: "center" }}>
                        {!isDuplicateSplit && (
                          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "0.25rem" }}>
                            <div className="insert-line-popover-container" style={{ position: "relative" }}>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveInsertMenu(activeInsertMenu?.itemId === item.id ? null : { itemId: item.id, index: idx });
                                }}
                                title="Insert line above or below"
                                style={{
                                  background: activeInsertMenu?.itemId === item.id ? "rgba(255, 255, 255, 0.1)" : "transparent",
                                  border: "none",
                                  color: activeInsertMenu?.itemId === item.id ? "var(--primary)" : "var(--text-secondary)",
                                  cursor: "pointer",
                                  padding: "0.2rem",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  borderRadius: "4px",
                                  transition: "all 0.15s ease"
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.color = "var(--primary)";
                                  e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.05)";
                                }}
                                onMouseLeave={(e) => {
                                  if (activeInsertMenu?.itemId !== item.id) {
                                    e.currentTarget.style.color = "var(--text-secondary)";
                                    e.currentTarget.style.backgroundColor = "transparent";
                                  }
                                }}
                              >
                                <ListPlus size={15} />
                              </button>

                              {activeInsertMenu?.itemId === item.id && (
                                <div 
                                  className="insert-line-popover animate-fade-in"
                                  style={{
                                    position: "absolute",
                                    right: 0,
                                    bottom: "100%",
                                    marginBottom: "6px",
                                    zIndex: 1000,
                                    backgroundColor: "var(--bg-card)",
                                    border: "1px solid var(--border-color)",
                                    borderRadius: "6px",
                                    boxShadow: "0 6px 20px rgba(0,0,0,0.5)",
                                    padding: "0.25rem",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "2px",
                                    minWidth: "135px"
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <button
                                    type="button"
                                    onClick={() => {
                                      handleInsertItem(idx, "above");
                                      setActiveInsertMenu(null);
                                    }}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "0.5rem",
                                      padding: "0.4rem 0.6rem",
                                      fontSize: "0.8rem",
                                      background: "transparent",
                                      border: "none",
                                      color: "var(--text-primary)",
                                      borderRadius: "4px",
                                      cursor: "pointer",
                                      textAlign: "left",
                                      width: "100%"
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.08)"}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                                  >
                                    <ArrowUp size={13} style={{ color: "var(--primary)" }} /> Insert Above
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      handleInsertItem(idx, "below");
                                      setActiveInsertMenu(null);
                                    }}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "0.5rem",
                                      padding: "0.4rem 0.6rem",
                                      fontSize: "0.8rem",
                                      background: "transparent",
                                      border: "none",
                                      color: "var(--text-primary)",
                                      borderRadius: "4px",
                                      cursor: "pointer",
                                      textAlign: "left",
                                      width: "100%"
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.08)"}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                                  >
                                    <ArrowDown size={13} style={{ color: "var(--primary)" }} /> Insert Below
                                  </button>
                                </div>
                              )}
                            </div>

                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => handleDeleteItem(item.id)}
                              style={{ padding: "0.2rem", minHeight: "auto", color: "var(--danger)", borderColor: "transparent", background: "transparent" }}
                              title="Delete Row"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: "1rem", display: "flex", justifyContent: "flex-start" }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleAddItem}
              style={{ display: "flex", alignItems: "center", gap: "0.4rem", padding: "0.4rem 0.8rem", fontSize: "0.85rem" }}
            >
              <Plus size={14} />
              Add Line Item
            </button>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="modal-footer" style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", borderTop: "1px solid var(--border-color)", padding: "1rem 1.5rem" }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </button>
          <button 
            type="button" 
            className="btn btn-secondary" 
            onClick={handleGeneratePDF} 
            disabled={isSaving}
          >
            Export PDF
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={isSaving} style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            {isSaving && <Loader2 size={16} className="spinner-icon" />}
            Save Changes
          </button>
        </div>

      <POLineMarkupModal
        isOpen={customMarkupModalOpen}
        onClose={() => {
          setCustomMarkupModalOpen(false);
          setSelectedMarkupItem(null);
        }}
        item={selectedMarkupItem}
        po={null}
        defaultMarkup={markupRate}
        onSave={handleSaveLineMarkup}
      />

      </div>
    </div>
  );
}
